import type { ParsedShotCode, ProjectConfig, ShotCodeParserConfig, ShotCodeSegmentConfig } from "../models/production";

export interface ShotCodeValidationResult {
  isValid: boolean;
  errors: string[];
  parsed?: ParsedShotCode;
}

export interface DerivedShotHierarchy {
  showCode?: string;
  seasonNumber?: number;
  episodeNumber?: number;
  seasonEpisodeCode?: string;
  sequenceNumber?: number;
  shotNumber?: number;
  sequenceCode?: string;
  episodeCode?: string;
}

export interface ParsedShotCodeBreadcrumb {
  segments: string[];
  showCodeLength: number;
  includeEpisode: boolean;
  seasonEpisodeLength: number;
  sequenceLength: number;
  shotLength: number;
}

export interface InferredShotCodeSettings {
  showCode?: string;
  shotCodeBreadcrumb: string;
  shotCodePattern: string;
  parserConfig: ShotCodeParserConfig;
}

export interface PlaceholderShotCodeOptions {
  showCode?: string | null;
  breadcrumb?: string | null;
  existingCodes?: string[];
}

const DEFAULT_CONFIG: ShotCodeParserConfig = {
  delimiter: "_",
  trimWhitespace: true,
  caseSensitive: false,
  segments: [
    { key: "showCode", meaning: "showCode", type: "alpha", length: 3 },
    {
      key: "seasonEpisodeCode",
      meaning: "seasonEpisodeCode",
      type: "numeric",
      length: 3,
      seasonEpisodeDecoder: { seasonDigits: 1, episodeDigits: 2 },
    },
    { key: "sequenceNumber", meaning: "sequenceNumber", type: "numeric", length: 3 },
    { key: "shotNumber", meaning: "shotNumber", type: "numeric", length: 4 },
  ],
};

function normalizeInput(input: string, config: ShotCodeParserConfig) {
  const trimmed = config.trimWhitespace === false ? input : input.trim();
  return config.caseSensitive ? trimmed : trimmed.toLowerCase();
}

export function parseShotCodeBreadcrumb(breadcrumb?: string | null): ParsedShotCodeBreadcrumb | null {
  if (!breadcrumb) return null;
  const segments = breadcrumb.trim().split("_");
  if (segments.length === 4) {
    const [showSegment, seasonEpisodeSegment, sequenceSegment, shotSegment] = segments;
    if (!/^@+$/.test(showSegment)) return null;
    if (![seasonEpisodeSegment, sequenceSegment, shotSegment].every((segment) => /^#+$/.test(segment))) return null;

    return {
      segments,
      includeEpisode: true,
      showCodeLength: showSegment.length,
      seasonEpisodeLength: seasonEpisodeSegment.length,
      sequenceLength: sequenceSegment.length,
      shotLength: shotSegment.length,
    };
  }

  if (segments.length === 3) {
    const [showSegment, sequenceSegment, shotSegment] = segments;
    if (!/^@+$/.test(showSegment)) return null;
    if (![sequenceSegment, shotSegment].every((segment) => /^#+$/.test(segment))) return null;

    return {
      segments,
      includeEpisode: false,
      showCodeLength: showSegment.length,
      seasonEpisodeLength: 0,
      sequenceLength: sequenceSegment.length,
      shotLength: shotSegment.length,
    };
  }

  return null;
}

export function validateShotCodeAgainstBreadcrumb(shotCode: string, breadcrumb?: string | null) {
  const parsedBreadcrumb = parseShotCodeBreadcrumb(breadcrumb);
  if (!parsedBreadcrumb) return null;

  const parts = shotCode.trim().split("_");
  const errors: string[] = [];
  const expectedSegmentCount = parsedBreadcrumb.includeEpisode ? 4 : 3;
  if (parts.length !== expectedSegmentCount) {
    return {
      isValid: false,
      errors: [`Shot code should have ${expectedSegmentCount} segments like ${breadcrumb.replace(/@/g, "A").replace(/#/g, "0")}`],
    };
  }

  const [showCode, maybeEpisodeOrSequence, maybeSequenceOrShot, maybeShot] = parts;
  if (showCode.length !== parsedBreadcrumb.showCodeLength || !/^[A-Za-z]+$/.test(showCode)) {
    errors.push(`Show code "${showCode}" should be ${parsedBreadcrumb.showCodeLength} letters.`);
  }

  const sequenceCode = parsedBreadcrumb.includeEpisode ? maybeSequenceOrShot : maybeEpisodeOrSequence;
  const shotNumber = parsedBreadcrumb.includeEpisode ? maybeShot : maybeSequenceOrShot;
  const seasonEpisodeCode = parsedBreadcrumb.includeEpisode ? maybeEpisodeOrSequence : "";

  if (parsedBreadcrumb.includeEpisode && (seasonEpisodeCode.length !== parsedBreadcrumb.seasonEpisodeLength || !/^\d+$/.test(seasonEpisodeCode))) {
    errors.push(`Episode segment "${seasonEpisodeCode}" should be ${parsedBreadcrumb.seasonEpisodeLength} digits.`);
  }
  if (sequenceCode.length !== parsedBreadcrumb.sequenceLength || !/^\d+$/.test(sequenceCode)) {
    errors.push(`Sequence segment "${sequenceCode}" should be ${parsedBreadcrumb.sequenceLength} digits.`);
  }
  if (shotNumber.length !== parsedBreadcrumb.shotLength || !/^\d+$/.test(shotNumber)) {
    errors.push(`Shot segment "${shotNumber}" should be ${parsedBreadcrumb.shotLength} digits.`);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

function validateSegmentValue(segment: ShotCodeSegmentConfig, value: string): string[] {
  const errors: string[] = [];
  if (segment.length !== undefined && value.length !== segment.length) {
    errors.push(`${segment.key} must be ${segment.length} characters`);
  }

  if (segment.type === "alpha" && !/^[a-z]+$/i.test(value)) {
    errors.push(`${segment.key} must contain letters only`);
  }
  if (segment.type === "numeric" && !/^\d+$/.test(value)) {
    errors.push(`${segment.key} must contain digits only`);
  }
  if (segment.type === "alphanumeric" && !/^[a-z0-9]+$/i.test(value)) {
    errors.push(`${segment.key} must be alphanumeric`);
  }
  if (segment.type === "literal" && segment.literalValue !== undefined && value !== segment.literalValue) {
    errors.push(`${segment.key} must equal ${segment.literalValue}`);
  }

  return errors;
}

function decodeSeasonEpisode(value: string, segment: ShotCodeSegmentConfig) {
  const decoder = segment.seasonEpisodeDecoder;
  if (!decoder) return {};

  const seasonValue = value.slice(0, decoder.seasonDigits);
  const episodeValue = value.slice(decoder.seasonDigits, decoder.seasonDigits + decoder.episodeDigits);

  return {
    seasonEpisodeCode: value,
    seasonNumber: seasonValue ? Number.parseInt(seasonValue, 10) : undefined,
    episodeNumber: episodeValue ? Number.parseInt(episodeValue, 10) : undefined,
  };
}

export function getShotCodeParserConfig(projectConfig?: Partial<ProjectConfig> | null): ShotCodeParserConfig {
  return projectConfig?.shotCodeParserConfig || DEFAULT_CONFIG;
}

export function inferShotCodeSettingsFromCodes(codes: string[]): InferredShotCodeSettings | null {
  const candidates = codes
    .map((code) => code.trim())
    .filter(Boolean)
    .map((code) => {
      const parts = code.split("_");
      if (parts.length === 4) {
        const [showCode, seasonEpisodeCode, sequenceCode, shotNumber] = parts;
        if (!/^[A-Za-z]+$/.test(showCode)) return null;
        if (![seasonEpisodeCode, sequenceCode, shotNumber].every((segment) => /^\d+$/.test(segment))) return null;
        return {
          code,
          showCode,
          includeEpisode: true,
          lengths: [showCode.length, seasonEpisodeCode.length, sequenceCode.length, shotNumber.length] as const,
        };
      }
      if (parts.length === 3) {
        const [showCode, sequenceCode, shotNumber] = parts;
        if (!/^[A-Za-z]+$/.test(showCode)) return null;
        if (![sequenceCode, shotNumber].every((segment) => /^\d+$/.test(segment))) return null;
        return {
          code,
          showCode,
          includeEpisode: false,
          lengths: [showCode.length, 0, sequenceCode.length, shotNumber.length] as const,
        };
      }
      return null;
    })
    .filter((candidate): candidate is { code: string; showCode: string; includeEpisode: boolean; lengths: readonly [number, number, number, number] } => Boolean(candidate));

  if (candidates.length === 0) return null;

  const signatureCounts = new Map<string, number>();
  candidates.forEach((candidate) => {
    const signature = candidate.lengths.join(":");
    signatureCounts.set(signature, (signatureCounts.get(signature) || 0) + 1);
  });

  const dominantSignature = Array.from(signatureCounts.entries()).sort((a, b) => b[1] - a[1])[0]?.[0];
  if (!dominantSignature) return null;

  const [showCodeLength, seasonEpisodeLength, sequenceLength, shotLength] = dominantSignature
    .split(":")
    .map((value) => Number.parseInt(value, 10));

  const matchingCandidates = candidates.filter((candidate) => candidate.lengths.join(":") === dominantSignature);
  const showCodeCounts = new Map<string, number>();
  matchingCandidates.forEach((candidate) => {
    const normalizedShowCode = candidate.showCode.toLowerCase();
    showCodeCounts.set(normalizedShowCode, (showCodeCounts.get(normalizedShowCode) || 0) + 1);
  });
  const inferredShowCode = Array.from(showCodeCounts.entries()).sort((a, b) => b[1] - a[1])[0]?.[0];

  const seasonDigits = Math.max(1, seasonEpisodeLength - 2);
  const episodeDigits = Math.max(1, seasonEpisodeLength - seasonDigits);
  const includeEpisode = seasonEpisodeLength > 0;
  const shotCodeBreadcrumb = includeEpisode
    ? `${"@".repeat(showCodeLength)}_${"#".repeat(seasonEpisodeLength)}_${"#".repeat(sequenceLength)}_${"#".repeat(shotLength)}`
    : `${"@".repeat(showCodeLength)}_${"#".repeat(sequenceLength)}_${"#".repeat(shotLength)}`;

  return {
    showCode: inferredShowCode,
    shotCodeBreadcrumb,
    shotCodePattern: shotCodeBreadcrumb,
    parserConfig: {
      delimiter: "_",
      trimWhitespace: true,
      caseSensitive: false,
      segments: includeEpisode
        ? [
            { key: "showCode", meaning: "showCode", type: "alpha", length: showCodeLength },
            {
              key: "seasonEpisodeCode",
              meaning: "seasonEpisodeCode",
              type: "numeric",
              length: seasonEpisodeLength,
              seasonEpisodeDecoder: { seasonDigits, episodeDigits },
            },
            { key: "sequenceNumber", meaning: "sequenceNumber", type: "numeric", length: sequenceLength },
            { key: "shotNumber", meaning: "shotNumber", type: "numeric", length: shotLength },
          ]
        : [
            { key: "showCode", meaning: "showCode", type: "alpha", length: showCodeLength },
            { key: "sequenceNumber", meaning: "sequenceNumber", type: "numeric", length: sequenceLength },
            { key: "shotNumber", meaning: "shotNumber", type: "numeric", length: shotLength },
          ],
    },
  };
}

export function buildPlaceholderShotCode({
  showCode,
  breadcrumb,
  existingCodes = [],
}: PlaceholderShotCodeOptions): string {
  const parsedBreadcrumb = parseShotCodeBreadcrumb(breadcrumb);
  if (!parsedBreadcrumb) {
    return showCode
      ? `${showCode}_${String(existingCodes.length + 1).padStart(4, "0")}`
      : `SH_${String(existingCodes.length + 1).padStart(4, "0")}`;
  }

  const normalizedShowCode = (showCode || "").trim() || "X".repeat(parsedBreadcrumb.showCodeLength);
  const baseSequence = "0".repeat(parsedBreadcrumb.sequenceLength);
  const normalizedExisting = new Set(existingCodes.map((code) => code.trim().toLowerCase()));

  for (let shotNumber = 0; shotNumber < 10 ** Math.min(parsedBreadcrumb.shotLength, 6); shotNumber += 1) {
    const candidateParts = [
      normalizedShowCode,
      ...(parsedBreadcrumb.includeEpisode ? ["0".repeat(parsedBreadcrumb.seasonEpisodeLength)] : []),
      baseSequence,
      String(shotNumber).padStart(parsedBreadcrumb.shotLength, "0"),
    ];
    const candidate = candidateParts.join("_");
    if (!normalizedExisting.has(candidate.toLowerCase())) {
      return candidate;
    }
  }

  const fallbackParts = [
    normalizedShowCode,
    ...(parsedBreadcrumb.includeEpisode ? ["0".repeat(parsedBreadcrumb.seasonEpisodeLength)] : []),
    baseSequence,
    "0".repeat(parsedBreadcrumb.shotLength),
  ];

  return fallbackParts.join("_");
}

export function parseShotCode(shotCode: string, parserConfig?: Partial<ShotCodeParserConfig> | null): ParsedShotCode {
  const config = { ...DEFAULT_CONFIG, ...parserConfig, segments: parserConfig?.segments || DEFAULT_CONFIG.segments };
  const normalized = normalizeInput(shotCode, config);
  const delimiter = config.delimiter ?? "_";
  const parts = normalized.split(delimiter);
  const parsed: ParsedShotCode = {
    original: shotCode,
    normalized,
    parts: {},
  };

  config.segments.forEach((segment, index) => {
    const value = parts[index] ?? "";
    parsed.parts[segment.key] = value;

    switch (segment.meaning) {
      case "showCode":
        parsed.showCode = value || parsed.showCode;
        break;
      case "seasonEpisodeCode":
        Object.assign(parsed, decodeSeasonEpisode(value, segment));
        break;
      case "seasonNumber":
        parsed.seasonNumber = value ? Number.parseInt(value, 10) : undefined;
        break;
      case "episodeNumber":
        parsed.episodeNumber = value ? Number.parseInt(value, 10) : undefined;
        break;
      case "sequenceNumber":
        parsed.sequenceNumber = value ? Number.parseInt(value, 10) : undefined;
        break;
      case "shotNumber":
        parsed.shotNumber = value ? Number.parseInt(value, 10) : undefined;
        break;
      default:
        break;
    }
  });

  return parsed;
}

export function validateShotCode(shotCode: string, parserConfig?: Partial<ShotCodeParserConfig> | null): ShotCodeValidationResult {
  const config = { ...DEFAULT_CONFIG, ...parserConfig, segments: parserConfig?.segments || DEFAULT_CONFIG.segments };
  const normalized = normalizeInput(shotCode, config);
  const delimiter = config.delimiter ?? "_";
  const parts = normalized.split(delimiter);
  const errors: string[] = [];

  if (parts.length !== config.segments.length) {
    errors.push(`Expected ${config.segments.length} segments separated by "${delimiter}"`);
  }

  config.segments.forEach((segment, index) => {
    const value = parts[index] ?? "";
    if (!value) {
      errors.push(`Missing ${segment.key}`);
      return;
    }
    errors.push(...validateSegmentValue(segment, value));
  });

  const parsed = parseShotCode(shotCode, config);

  return {
    isValid: errors.length === 0,
    errors,
    parsed,
  };
}

export function deriveHierarchyFromShotCode(shotCode: string, parserConfig?: Partial<ShotCodeParserConfig> | null): DerivedShotHierarchy {
  const parsed = parseShotCode(shotCode, parserConfig);
  const seasonEpisodeCode = parsed.seasonEpisodeCode
    || (parsed.seasonNumber !== undefined && parsed.episodeNumber !== undefined
      ? `${parsed.seasonNumber}${String(parsed.episodeNumber).padStart(2, "0")}`
      : undefined);

  return {
    showCode: parsed.showCode,
    seasonNumber: parsed.seasonNumber,
    episodeNumber: parsed.episodeNumber,
    seasonEpisodeCode,
    sequenceNumber: parsed.sequenceNumber,
    shotNumber: parsed.shotNumber,
    episodeCode: seasonEpisodeCode ? `${parsed.showCode || ""}_${seasonEpisodeCode}` : undefined,
    sequenceCode:
      seasonEpisodeCode && parsed.sequenceNumber !== undefined
        ? `${parsed.showCode || ""}_${seasonEpisodeCode}_${String(parsed.sequenceNumber).padStart(3, "0")}`
        : undefined,
  };
}
